import React from "react";
import { List } from "react-toolbox/lib/list";
import { PageSelector } from "./PageSelector";
import style from "./style.scss";

export interface PagingInfo<TItem> {
    /**
     * The items that are in the list on the current page.
     */
    readonly items: readonly TItem[];
    /**
     * The total number of items.
     */
    readonly total: number;
    readonly pageSize: number;
    readonly page: number;
}

export interface DynamicPagingListProps<Item> {
    readonly data: PagingInfo<Item>;
    readonly activeItem?: Item;
    /**
     * Callback when the user selects an item in the list.
     *
     * If not provided, you can register your own in the renderItem method.
     *
     * @param item
     */
    onItemSelect?(item: Item): void;
    onPageChange(page: number): void;
    renderItem(item: Item): JSX.Element;
}

/**
 * A paging list in which all the items are currently not available (because the next page needs to be downloaded etc.).
 */
export function DynamicPagingList<Item>(props: DynamicPagingListProps<Item>): JSX.Element {
    const { data, onPageChange } = props;
    const { items, total, page, pageSize } = data;

    const totalPages = typeof total === "number" ? Math.ceil(total / pageSize) : 1;

    function handleItemSelect(item: Item) {
        if (props.onItemSelect) {
            props.onItemSelect(item);
        }
    }

    return (
        <>
            <div className={style.panel}>
                <List>{(items || []).map((item, index) => <PagingListItem
                    key={index}
                    item={item}
                    renderItem={props.renderItem}
                    onSelect={handleItemSelect}
                />)}</List>
            </div>

            <PageSelector
                selectedPage={page}
                totalPages={totalPages}
                onPageChange={onPageChange}
            />
        </>
    );
}

interface PagingListItemProps<Item> {
    readonly item: Item;
    renderItem(item: Item): JSX.Element;
    onSelect(item: Item): void;
}

function PagingListItem<Item>(props: PagingListItemProps<Item>) {

    function handleClick() {
        props.onSelect(props.item);
    }

    return <div onClick={handleClick}>{props.renderItem(props.item)}</div>;
}

export default DynamicPagingList;
